import { cva } from 'class-variance-authority';
import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { Drawer } from 'vaul';

import type {
  IBottomDrawerProps,
  IDrawerContentProps,
  IDrawerNesteRootProps,
  IDrawerOverlayProps,
  IDrawerPortalProps,
  IDrawerRootProps,
  IDrawerTitleProps,
  IDrawerTriggerProps,
} from './IBottomDrawerProps';

export const drawerContentVariants = cva(
  'fixed inset-x-0  bottom-0 mt-24 flex h-full flex-col rounded-t-[20px] bg-white',
  {
    variants: {
      mode: {
        half: 'max-h-[50%]',
        full: 'max-h-[94%] rounded-none',
      },
    },
    defaultVariants: {
      mode: 'half',
    },
  }
);

const DrawerRoot = (props: IDrawerRootProps) => {
  const { children, onClose, ...rest } = props;
  return (
    <Drawer.Root
      onClose={() => {
        onClose?.();
        setTimeout(() => {
          // @ts-ignore
          document.body.style = '';
        }, 0);
      }}
      {...rest}
    >
      {children}
    </Drawer.Root>
  );
};

DrawerRoot.displayName = 'DrawerRoot';

const DrawerPortal = (props: IDrawerPortalProps) => {
  const { children, ...rest } = props;
  return <Drawer.Portal {...rest}>{children}</Drawer.Portal>;
};

DrawerPortal.displayName = 'DrawerPortal';

const DrawerOverlay = forwardRef<
  React.ElementRef<typeof Drawer.Overlay>,
  IDrawerOverlayProps
>((props, forwardedRef) => {
  const { children, className, ...rest } = props;
  return (
    <Drawer.Overlay
      className={twMerge('fixed inset-0 z-50 bg-black/40', className)}
      ref={forwardedRef}
      {...rest}
    >
      {children}
    </Drawer.Overlay>
  );
});

DrawerOverlay.displayName = 'DrawerOverlay';

const DrawerContent = forwardRef<
  React.ElementRef<typeof Drawer.Content>,
  IDrawerContentProps
>((props, forwardedRef) => {
  const { children, className, mode, ...rest } = props;
  return (
    <Drawer.Content
      className={twMerge(drawerContentVariants({ mode }), className)}
      ref={forwardedRef}
      {...rest}
    >
      {children}
    </Drawer.Content>
  );
});

DrawerContent.displayName = 'DrawerContent';

const DrawerTrigger = forwardRef<
  React.ElementRef<typeof Drawer.Trigger>,
  IDrawerTriggerProps
>((props, forwardedRef) => {
  const { children, ...rest } = props;
  return (
    <Drawer.Trigger ref={forwardedRef} {...rest}>
      {children}
    </Drawer.Trigger>
  );
});

DrawerTrigger.displayName = 'DrawerTrigger';

const DrawerNestedRoot = (props: IDrawerNesteRootProps) => {
  const { children, ...rest } = props;
  return <Drawer.NestedRoot {...rest}>{children}</Drawer.NestedRoot>;
};

DrawerNestedRoot.displayName = 'DrawerNestedRoot';

const DrawerTitle = (props: IDrawerTitleProps) => {
  const { children, ...rest } = props;
  return <Drawer.Title {...rest}>{children}</Drawer.Title>;
};

DrawerTitle.displayName = 'DrawerTitle';

const BottomDrawer = (props: IBottomDrawerProps) => {
  const {
    open = true,
    onOpenChange = () => {},
    children,
    className,
    dismissable = true,
    onClose = () => {},
    ...rest
  } = props;

  return (
    <Drawer.Root
      dismissible={dismissable}
      open={open}
      onOpenChange={onOpenChange}
      onClose={onClose}
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Drawer.Content
          {...rest}
          className={twMerge(
            'fixed inset-x-0 bottom-0 mt-24 flex h-full max-h-[50%] flex-col rounded-t-[20px] bg-white',
            className
          )}
        >
          <>
            <div className="flex w-full items-center justify-center py-4">
              <div
                aria-hidden="true"
                className="bg-mobile-gray-400  h-1 w-14 rounded"
              />
            </div>
            {children}
          </>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default BottomDrawer;
export {
  Drawer,
  DrawerContent,
  DrawerNestedRoot,
  DrawerOverlay,
  DrawerPortal,
  DrawerRoot,
  DrawerTitle,
  DrawerTrigger,
};
